import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  auth,
  messaging,
  getToken,
  onMessage,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  reload,
  googleProvider,
  signInWithPopup,
} from '../config/firebase';
import { clearRecentlyViewed } from '../utils/recentlyViewed';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // Update axios headers when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token]);

  // Capture beforeinstallprompt so we can trigger it at the right moment.
  // index.js stashes any early-fired event in window.__pwaInstallPrompt so we
  // don't miss it if it fired before React mounted.
  useEffect(() => {
    if (window.__pwaInstallPrompt) {
      setInstallPrompt(window.__pwaInstallPrompt);
      window.__pwaInstallPrompt = null;
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Clear prompt once the app is installed
    window.addEventListener('appinstalled', () => setInstallPrompt(null));
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  // ── FCM Web Push ────────────────────────────────────────────────────────────

  /**
   * Register an FCM web push token for the current user.
   * Called contextually (after a match, on profile page) rather than immediately
   * on login, to maximise permission grant rate.
   */
  const registerWebPushToken = useCallback(async () => {
    if (!messaging) { console.warn('[Push] messaging is null — FCM not supported in this env'); return; }
    if (!token) { console.warn('[Push] no auth token — skipping'); return; }
    if (!('Notification' in window)) { console.warn('[Push] Notification API not available'); return; }
    if (Notification.permission !== 'granted') { console.warn('[Push] permission not granted:', Notification.permission); return; }

    try {
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) { console.warn('[Push] REACT_APP_FIREBASE_VAPID_KEY not set'); return; }
      // Use the already-active SW registration so FCM doesn't try to register
      // firebase-messaging-sw.js (which gets stuck in "waiting" behind service-worker.js).
      const swReg = await navigator.serviceWorker.ready;
      console.log('[Push] calling getToken with active SW:', swReg.active?.scriptURL);
      const fcmToken = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg });
      if (fcmToken) {
        console.log('[Push] token obtained, registering with backend');
        await api.post('/users/push-token', { token: fcmToken, platform: 'web' });
        console.log('[Push] token registered successfully');
      } else {
        console.warn('[Push] getToken returned empty — SW not ready or permission issue');
      }
    } catch (err) {
      console.error('[Push] getToken failed:', err?.code, err?.message, err);
    }
  }, [token, messaging]);

  /**
   * Request notification permission, then register the FCM token.
   * Call this from a high-value moment (e.g. first match reveal).
   */
  const requestPushPermission = useCallback(async () => {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'denied') return false;
    try {
      const result = await Notification.requestPermission();
      if (result === 'granted') {
        await registerWebPushToken();
        return true;
      }
    } catch {
      // Safari older versions use callback style — ignore
    }
    return false;
  }, [registerWebPushToken]);

  // ── Firebase auth state ──────────────────────────────────────────────────────

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser && !localStorage.getItem('token')) {
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;

    try {
      const [notifResponse, countResponse, messagesCountResponse] = await Promise.all([
        api.get('/notifications/'),
        api.get('/notifications/unread-count'),
        api.get('/messages/unread-count/total'),
      ]);
      setNotifications(notifResponse.data);
      setUnreadCount(countResponse.data.count);
      setUnreadMessagesCount(messagesCountResponse.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  // Handle foreground FCM messages — show in-app toast / refresh badges
  // Must be after fetchNotifications declaration to avoid temporal dead zone.
  useEffect(() => {
    if (!messaging || !user) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      // Refresh notification badge so the count stays accurate
      fetchNotifications();
      // Optionally show a browser notification if permission granted
      if (Notification.permission === 'granted') {
        // Payload is data-only — title/body live in data (see backend/integrations/push/fcm_web.py).
        const data = payload.data ?? {};
        const title = data.title || 'WANTD';
        const body = data.body || '';
        // Only show if the document is hidden (user is on another tab)
        if (document.hidden) {
          new Notification(title, {
            body,
            icon: '/icons/icon-192.png',
          });
        }
      }
    });
    return () => unsubscribe();
  }, [messaging, user, fetchNotifications]);

  // Refresh badges when the service worker relays a background push (tab open but unfocused).
  useEffect(() => {
    if (!user || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (event) => {
      const src = event.data?.source;
      if (src === 'push' || src === 'fcm-background') {
        fetchNotifications();
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [user, fetchNotifications]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    // Push fills the realtime gap; poll is a safety net for evicted SWs.
    const interval = setInterval(fetchNotifications, 120000);
    // Refetch immediately when the tab becomes visible (resume-from-sleep, tab-switch-back).
    const onVisibility = () => {
      if (!document.hidden) fetchNotifications();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, fetchNotifications]);

  // If the user has already granted notification permission (e.g. from a prior
  // session), re-register the FCM token on every login so the backend always
  // has a fresh, valid token for this device.
  useEffect(() => {
    if (user) registerWebPushToken();
  }, [user, registerWebPushToken]);

  // Firebase login
  const login = async (email, password) => {
    // Sign in with Firebase
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    const idToken = await userCredential.user.getIdToken();
    
    // Exchange Firebase token for our backend token
    const response = await axios.post(`${API_URL}/api/auth/firebase/login`, {
      id_token: idToken
    });
    
    const { access_token, user: userData } = response.data;
    
    // Check email verification - superadmins are exempt (their role can't be self-granted)
    const isSuperAdmin = userData.role === 'superadmin';
    if (!isSuperAdmin && !userCredential.user.emailVerified) {
      await firebaseSignOut(auth);
      const error = new Error('Please verify your email before signing in. Check your inbox for a verification link.');
      error.code = 'auth/email-not-verified';
      throw error;
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Notify theme context that user is now authenticated
    window.dispatchEvent(new Event('authChange'));
    
    // Return user data including role for redirect handling
    return { user: userData, isAdmin: userData.role === 'admin' || userData.role === 'superadmin' };
  };

  // Firebase register - creates account and sends verification email
  const register = async (userData) => {
    const { email, password, username, first_name, last_name, account_type, location_name } = userData;
    
    let userCredential = null;
    
    try {
      // Create Firebase user
      userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // NOTE: We no longer call Firebase's sendEmailVerification() here
      // The backend will send a branded verification email via Resend after registration
      
      // Get token and register with backend (creates user record, but user won't be able to login until verified)
      const idToken = await userCredential.user.getIdToken();
      
      // Register with our backend
      // Use a timeout since this can sometimes be slow, but the registration
      // will likely complete even if we timeout
      try {
        await axios.post(`${API_URL}/api/auth/firebase/register`, {
          id_token: idToken,
          username,
          first_name,
          last_name,
          account_type: account_type || 'both',
          location_name
        }, { timeout: 30000 }); // 30 second timeout
      } catch (backendError) {
        // If we get a timeout or network error, the registration might still succeed
        // Log it but don't fail - the user can try logging in after verifying email
        if (backendError.code === 'ECONNABORTED' || backendError.message?.includes('timeout')) {
          console.warn('Backend registration timed out, but Firebase account was created. User should verify email and try logging in.');
        } else if (backendError.response?.status === 504) {
          console.warn('Backend gateway timeout, but Firebase account was created. User should verify email and try logging in.');
        } else {
          // For other errors (e.g., username taken), we need to clean up the Firebase user
          // to prevent orphaned Firebase accounts
          console.error('Backend registration failed:', backendError);
          if (userCredential?.user) {
            try {
              await userCredential.user.delete();
              console.log('Cleaned up Firebase user after backend registration failure');
            } catch (deleteError) {
              console.error('Failed to delete Firebase user after backend error:', deleteError);
            }
          }
          throw backendError;
        }
      }
      
      // Sign out - user must verify email before logging in
      await firebaseSignOut(auth);
      
      // Return success without logging in - user needs to verify email first
      return { 
        requiresVerification: true,
        email: email,
        message: 'Please check your email to verify your account before signing in.'
      };
    } catch (error) {
      // If Firebase user was created but something else failed, try to clean up
      if (userCredential?.user && error.code !== 'auth/email-already-in-use') {
        try {
          await userCredential.user.delete();
          console.log('Cleaned up Firebase user after registration failure');
        } catch (deleteError) {
          console.error('Failed to delete Firebase user:', deleteError);
        }
      }
      throw error;
    }
  };

  // Complete registration after email verification
  const completeRegistration = async (userData) => {
    const { username, account_type, location_name } = userData;
    
    // Get current Firebase user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No Firebase user found. Please sign in first.');
    }
    
    // Reload to get latest emailVerified status
    await reload(currentUser);
    
    if (!currentUser.emailVerified) {
      throw new Error('Email not verified yet. Please check your inbox and click the verification link.');
    }
    
    // Get fresh token
    const idToken = await currentUser.getIdToken(true);
    
    // Register with our backend
    const response = await axios.post(`${API_URL}/api/auth/firebase/register`, {
      id_token: idToken,
      username,
      account_type: account_type || 'both',
      location_name
    });
    
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    
    // Notify theme context
    window.dispatchEvent(new Event('authChange'));
    
    return { user: newUser };
  };

  // Password reset
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  };

  // Direct token login - used for OAuth providers like Facebook
  const loginWithToken = async (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    
    // Notify theme context that user is now authenticated
    window.dispatchEvent(new Event('authChange'));
    
    return { user: userData, isAdmin: userData.role === 'admin' || userData.role === 'superadmin' };
  };

  // Google Sign-In via Firebase
  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      // Get Firebase ID token
      const idToken = await firebaseUser.getIdToken();
      
      // Try to login first
      try {
        const response = await axios.post(`${API_URL}/api/auth/firebase/login`, {
          id_token: idToken,
          email: firebaseUser.email,
          display_name: firebaseUser.displayName,
          photo_url: firebaseUser.photoURL,
          provider: 'google'
        });
        
        const { access_token, user: userData, inactive_inventory } = response.data;
        
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        setFirebaseUser(firebaseUser);
        
        // Notify theme context
        window.dispatchEvent(new Event('authChange'));
        
        const isAdmin = userData.role === 'admin' || userData.role === 'superadmin';
        
        return { 
          user: userData, 
          isAdmin, 
          inactiveInventory: inactive_inventory 
        };
      } catch (loginError) {
        // If 404, user doesn't exist - auto-register them
        if (loginError.response?.status === 404) {
          // Generate username from email or display name
          const baseUsername = firebaseUser.displayName?.replace(/\s+/g, '_').toLowerCase() 
            || firebaseUser.email?.split('@')[0] 
            || 'user';
          const username = `${baseUsername}_${Date.now().toString(36).slice(-4)}`;
          
          // Auto-register with Google account info
          const registerResponse = await axios.post(`${API_URL}/api/auth/firebase/register`, {
            id_token: idToken,
            username: username,
            first_name: firebaseUser.displayName?.split(' ')[0] || '',
            last_name: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
            account_type: 'both'
          });
          
          const { access_token, user: userData } = registerResponse.data;
          
          localStorage.setItem('token', access_token);
          setToken(access_token);
          setUser(userData);
          setFirebaseUser(firebaseUser);
          
          window.dispatchEvent(new Event('authChange'));
          
          return { 
            user: userData, 
            isAdmin: false, 
            isNewUser: true 
          };
        }
        throw loginError;
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Pop-up was blocked. Please allow pop-ups for this site.');
      }
      
      throw error;
    }
  };

  const reactivateInventory = async () => {
    const response = await api.post('/inventory/reactivate-all');
    return response.data;
  };

  const logout = async () => {
    // Remove FCM web push token so this device stops receiving notifications
    if (messaging && token) {
      try {
        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        if (vapidKey) {
          const fcmToken = await getToken(messaging, { vapidKey }).catch(() => null);
          if (fcmToken) {
            await api.delete('/users/push-token', { data: { token: fcmToken } }).catch(() => {});
          }
        }
      } catch {
        // Non-fatal — proceed with logout regardless
      }
    }

    // Sign out from Firebase
    if (firebaseUser) {
      try {
        await firebaseSignOut(auth);
      } catch (error) {
        console.error('Firebase sign out error:', error);
      }
    }

    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setFirebaseUser(null);
    setNotifications([]);
    setUnreadCount(0);
    setUnreadMessagesCount(0);
    // Notify theme context to switch to light mode
    window.dispatchEvent(new Event('authChange'));
    // Clear AI processing state
    window.dispatchEvent(new Event('clearAIProcessing'));
    // Clear recently viewed so next user starts fresh
    clearRecentlyViewed();
  };

  const updateProfile = async (data) => {
    await api.put('/auth/profile', null, { params: data });
    await fetchUser();
  };

  const markNotificationRead = async (notificationId) => {
    await api.post(`/notifications/${notificationId}/read`);
    await fetchNotifications();
  };

  const markAllNotificationsRead = async () => {
    await api.post('/notifications/read-all');
    await fetchNotifications();
  };

  // Link existing account to Firebase
  const linkFirebaseAccount = async () => {
    if (!firebaseUser) {
      throw new Error('No Firebase user signed in');
    }
    const idToken = await firebaseUser.getIdToken();
    const response = await api.post('/auth/firebase/link-account', { id_token: idToken });
    return response.data;
  };

  // Resend verification email via custom Resend API
  const resendVerificationEmail = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No user signed in');
    }
    
    // Use our custom Resend-based verification email
    await axios.post(`${API_URL}/api/auth/custom/send-verification`, {
      email: currentUser.email
    });
    
    return { success: true };
  };

  // Check if current Firebase user's email is verified
  const checkEmailVerified = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }
    await reload(currentUser);
    return currentUser.emailVerified;
  };

  const value = {
    user,
    token,
    loading,
    notifications,
    unreadCount,
    unreadMessagesCount,
    api,
    login,
    loginWithToken,
    loginWithGoogle,
    register,
    logout,
    updateProfile,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    reactivateInventory,
    refreshUser: fetchUser,
    // PWA install prompt
    installPrompt,
    clearInstallPrompt: () => setInstallPrompt(null),
    // FCM web push
    requestPushPermission,
    registerWebPushToken,
    // Firebase-specific
    firebaseUser,
    resetPassword,
    linkFirebaseAccount,
    completeRegistration,
    resendVerificationEmail,
    checkEmailVerified,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
